import { useMemo } from 'react';

const TIME_ONLY_PATTERN = /^\d{1,2}:\d{2}(?::\d{2})?$/;
const HAS_TIME_ZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;

const pad = (value) => String(value).padStart(2, '0');

const parseUtcDateTime = (value, utcDate) => {
  if (!value && value !== 0) return null;

  if (value instanceof Date) {
    const date = new Date(value.getTime());
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'object') return null;

  let normalizedValue = String(value).trim().replace(' ', 'T');

  if (TIME_ONLY_PATTERN.test(normalizedValue)) {
    if (!utcDate) return null;
    normalizedValue = `${String(utcDate).slice(0, 10)}T${normalizedValue}`;
  }

  if (!HAS_TIME_ZONE_PATTERN.test(normalizedValue)) {
    normalizedValue = `${normalizedValue}Z`;
  }

  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const createLocalDateTime = (dateValue, timeValue) => {
  const dateParts = String(dateValue).slice(0, 10).split('-').map(Number);
  const timeParts = String(timeValue).split(':').map(Number);

  if (
    dateParts.length !== 3 ||
    timeParts.length < 2 ||
    [...dateParts, ...timeParts].some(Number.isNaN)
  ) {
    return null;
  }

  const [year, month, day] = dateParts;
  const [hours, minutes, seconds = 0] = timeParts;
  const date = new Date(year, month - 1, day, hours, minutes, seconds);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateKey = (year, month, day) =>
  Date.UTC(year, month - 1, day) / (24 * 60 * 60 * 1000);

const utcToLocalDateTime = (utcDateTime, utcDate) =>
  parseUtcDateTime(utcDateTime, utcDate);

const localDateTimeToUtc = (dateValue, timeValue) => {
  const localDateTime = createLocalDateTime(dateValue, timeValue);
  return localDateTime ? localDateTime.toISOString() : null;
};

const localDateTimeToUtcParts = (dateValue, timeValue) => {
  const localDateTime = createLocalDateTime(dateValue, timeValue);

  if (!localDateTime) return null;

  const utcDate = [
    localDateTime.getUTCFullYear(),
    pad(localDateTime.getUTCMonth() + 1),
    pad(localDateTime.getUTCDate()),
  ].join('-');
  const utcTime = `${pad(localDateTime.getUTCHours())}:${pad(
    localDateTime.getUTCMinutes()
  )}`;
  const localDateKey = getDateKey(
    localDateTime.getFullYear(),
    localDateTime.getMonth() + 1,
    localDateTime.getDate()
  );
  const utcDateKey = getDateKey(
    localDateTime.getUTCFullYear(),
    localDateTime.getUTCMonth() + 1,
    localDateTime.getUTCDate()
  );

  return {
    date: utcDate,
    time: utcTime,
    iso: localDateTime.toISOString(),
    dayShift: utcDateKey - localDateKey,
  };
};

const formatUtcToLocalString = (utcDateTime) => {
  const localDateTime = utcToLocalDateTime(utcDateTime);

  if (!localDateTime) return 'Time Unknown';

  return localDateTime
    .toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
    .toLowerCase();
};

const formatDateForSchedulePage = (dateTime) => {
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  if (Number.isNaN(date.getTime())) return 'Invalid Time';

  const day = date.getDate();
  const suffix =
    day >= 11 && day <= 13
      ? 'th'
      : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
  const month = date.toLocaleDateString('en-US', { month: 'short' });

  return `${month} ${day}${suffix}, ${date.getFullYear()}`;
};

const formattedAppointmentTime = (utcStartDate, utcEndDate) => {
  const startLocal = utcToLocalDateTime(utcStartDate);
  const endLocal = utcToLocalDateTime(utcEndDate);

  if (!startLocal || !endLocal) return 'Time Unknown';

  return `${formatDateForSchedulePage(startLocal)} ${formatUtcToLocalString(
    utcStartDate
  )} - ${formatUtcToLocalString(utcEndDate)}`;
};

const formatPerformanceDate = (utcDateTime) => {
  const localDateTime = utcToLocalDateTime(utcDateTime);
  if (!localDateTime) return 'Invalid Time';

  const date = localDateTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${date}; ${formatUtcToLocalString(utcDateTime)}`;
};

export const useDateTime = () =>
  useMemo(
    () => ({
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utcToLocalDateTime,
      localDateTimeToUtc,
      localDateTimeToUtcParts,
      formatUtcToLocalString,
      formatDateForSchedulePage,
      formattedAppointmentTime,
      formatPerformanceDate,
    }),
    []
  );

export { utcToLocalDateTime, localDateTimeToUtcParts };
